import { create } from "zustand";

import type {
  GetProvidersZocdocProvidersGetParams,
  ZocdocProviderResult,
  ZocdocTimeslot,
} from "@/api/generated/models";

export type ZocdocBookingContent = {
  specialty_id: string;
  visit_reason_id: string;
  insurance_plan_id?: string;
  final?: boolean;
};

export type ZocdocPhase =
  | "insurance"
  | "zip"
  | "providers"
  | "availability"
  | "day"
  | "confirm";

type ZocdocBookingStore = {
  isOpen: boolean;
  bookingContent: ZocdocBookingContent | null;
  eventId: string | null;
  phase: ZocdocPhase;
  selectedPlanId: string | null;
  selectedCarrierName: string | null;
  zipCode: string;
  selectedProvider: ZocdocProviderResult | null;
  providerParams: GetProvidersZocdocProvidersGetParams | null;
  weekStartDate: string | null;
  selectedDate: string | null;
  selectedTimeslot: ZocdocTimeslot | null;

  openForContent: (
    content: ZocdocBookingContent,
    eventId: string,
    initialPhase?: ZocdocPhase,
  ) => void;
  close: () => void;
  reset: () => void;

  setPhase: (phase: ZocdocPhase) => void;
  setSelectedPlanId: (id: string | null) => void;
  setSelectedCarrierName: (name: string | null) => void;
  setZipCode: (zip: string) => void;
  setSelectedProvider: (provider: ZocdocProviderResult | null) => void;
  setProviderParams: (
    params: GetProvidersZocdocProvidersGetParams | null,
  ) => void;
  setWeekStartDate: (date: string | null) => void;
  setSelectedDate: (date: string | null) => void;
  setSelectedTimeslot: (slot: ZocdocTimeslot | null) => void;
};

const initialState = {
  isOpen: false,
  bookingContent: null,
  eventId: null,
  phase: "insurance" as ZocdocPhase,
  selectedPlanId: null,
  selectedCarrierName: null,
  zipCode: "",
  selectedProvider: null,
  providerParams: null,
  weekStartDate: null,
  selectedDate: null,
  selectedTimeslot: null,
};

export const useZocdocBookingStore = create<ZocdocBookingStore>((set, get) => ({
  ...initialState,

  openForContent: (content, eventId, initialPhase) => {
    const current = get().bookingContent;
    const isSameBooking =
      current !== null &&
      current.specialty_id === content.specialty_id &&
      current.visit_reason_id === content.visit_reason_id;
    if (isSameBooking) {
      set({
        isOpen: true,
        eventId,
        ...(initialPhase ? { phase: initialPhase } : {}),
      });
    } else {
      set({
        ...initialState,
        bookingContent: content,
        eventId,
        isOpen: true,
        ...(initialPhase ? { phase: initialPhase } : {}),
      });
    }
  },

  close: () => set({ isOpen: false }),
  reset: () => set({ ...initialState }),

  setPhase: (phase) => set({ phase }),
  setSelectedPlanId: (selectedPlanId) => set({ selectedPlanId }),
  setSelectedCarrierName: (selectedCarrierName) => set({ selectedCarrierName }),
  setZipCode: (zipCode) => set({ zipCode }),
  setSelectedProvider: (selectedProvider) => set({ selectedProvider }),
  setProviderParams: (providerParams) => set({ providerParams }),
  setWeekStartDate: (weekStartDate) => set({ weekStartDate }),
  setSelectedDate: (selectedDate) => set({ selectedDate }),
  setSelectedTimeslot: (selectedTimeslot) => set({ selectedTimeslot }),
}));
